import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowLeft, ShieldAlert, MapPin, CheckCircle2, ChevronRight, ChevronDown, Zap, Gift, Plus, Mail, X } from 'lucide-react'; 
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { cartService } from '../services/cartService';
import { customerService } from '../services/customerService'; 
import catalogService from '../services/catalogService'; 
import itemService from '../services/itemService'; 
import { getImageUrl, generateSlug } from '../utils/helpers';
import { toast } from 'react-toastify';
import { useCart } from '../contexts/CartContext';

// 👉 TỐI ƯU: Component Accordion giữ nguyên UI gốc của bạn
const PromotionAccordion = React.memo(({ promo }) => {
    const [isOpen, setIsOpen] = useState(false);
    const promoName = typeof promo === 'string' ? promo : (promo.saleName || promo.name);
    
    if (!promoName) return null;

    return (
        <div className="flex flex-col border border-orange-200/60 rounded-lg overflow-hidden bg-gradient-to-r from-orange-100 to-white w-full mb-1.5 last:mb-0 transition-all shadow-sm">
            <button
                type="button"
                onClick={(e) => { 
                    e.preventDefault(); 
                    e.stopPropagation(); 
                    if(promo.description) setIsOpen(!isOpen); 
                }}
                className={`flex items-center justify-between gap-2 px-2.5 py-2 w-full text-left transition-colors ${promo.description ? 'hover:bg-orange-50 cursor-pointer' : 'cursor-default'}`}
            >
                <div className="flex items-start gap-1.5 text-xs text-tnc-orange w-full min-w-0">
                    <Gift size={14} className="shrink-0 text-tnc-orange mt-0.5" />
                    <span className="font-bold leading-snug line-clamp-2">{promoName}</span>
                </div>
                {promo.description && (
                    <ChevronDown size={14} className={`text-green-600 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                )}
            </button>
            
            {isOpen && promo.description && (
                <div className="px-2.5 pb-2.5 pt-0.5 text-[11px] text-gray-700 bg-white border-t border-green-50">
                    <div 
                        className="html-content mt-1.5 [&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md"
                        dangerouslySetInnerHTML={{ __html: String(promo.description).replace(/&nbsp;/g, ' ') }} 
                    />
                </div>
            )}
        </div>
    );
});

const CartPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fetchCart: fetchGlobalCart } = useCart();

    const [cartItems, setCartItems] = useState([]);
    const [allSales, setAllSales] = useState([]); 
    const [allProducts, setAllProducts] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(true);

    const [selectedItemIds, setSelectedItemIds] = useState([]);
    
    // States cho quản lý địa chỉ
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState('');
    const [isAddrOpen, setIsAddrOpen] = useState(false); 
    const [tempAddress, setTempAddress] = useState(null); // Địa chỉ tạm vừa tạo qua Modal

    // States cho Modal Địa chỉ
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [isSavingAddress, setIsSavingAddress] = useState(false);
    const [addressForm, setAddressForm] = useState({
        receiverName: '',
        receiverPhone: '',
        receiverEmail: '',
        province: '',
        ward: '',
        specificAddress: '',
        saveToProfile: true
    });
    
    const debounceTimers = useRef({});

    const fetchUserAddresses = useCallback(async (preserveSelection = false) => {
        try {
            const res = await customerService.getAddresses();
            const addrList = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            setAddresses(addrList);
            
            // Dùng callback của setState để không cần đưa selectedAddressId vào mảng dependency
            setSelectedAddressId(prevId => {
                if (!preserveSelection || !prevId) {
                    const defaultAddr = addrList.find(a => a.isDefault) || addrList[0];
                    return defaultAddr ? defaultAddr.addressId.toString() : prevId;
                }
                return prevId;
            });
        } catch (error) { 
            console.error("Lỗi lấy địa chỉ", error); 
        }
    }, []);

    const fetchCartData = useCallback(async () => {
        try {
            const [cartRes, salesRes, itemsRes] = await Promise.all([
                cartService.getCart(),
                catalogService.getPublicSales().catch(() => []),
                itemService.getPublicItems().catch(() => []) 
            ]);

            setCartItems(Array.isArray(cartRes?.data) ? cartRes.data : (Array.isArray(cartRes) ? cartRes : []));
            setAllSales(Array.isArray(salesRes?.data) ? salesRes.data : (Array.isArray(salesRes) ? salesRes : []));
            setAllProducts(Array.isArray(itemsRes?.data) ? itemsRes.data : (Array.isArray(itemsRes) ? itemsRes : []));
        } catch (error) { console.error("Lỗi lấy dữ liệu:", error); } 
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) { setIsLoggedIn(false); setLoading(false); return; }
        fetchCartData();
        fetchUserAddresses();
    }, [fetchCartData, fetchUserAddresses]);

    useEffect(() => {
        if (location.state?.buyNowItemId && cartItems.length > 0) {
            const targetId = String(location.state.buyNowItemId);
            const target = cartItems.find(i => String(i.itemId || i.item?.itemId || i.id || i.productId) === targetId);
            if (target && target.cartItemId) {
                setSelectedItemIds([target.cartItemId]);
                navigate(location.pathname, { replace: true, state: {} });
            }
        }
    }, [cartItems, location.state, location.pathname, navigate]);

    const { validItemsCount, selectedCartItems, totalPrice, totalSelectedQuantity, currentAddress } = useMemo(() => {
        const validItems = cartItems.filter(item => {
            const limit = item.isFlashSaleLine ? (item.itemFlashSaleQuantity || 0) : item.maxQuantity;
            return (item.itemStatus === 2 || item.itemStatus === 3) && Number(item.itemPrice) > 0 && item.maxQuantity > 0 && item.quantity <= limit;
        });

        const selected = cartItems.filter(item => selectedItemIds.includes(item.cartItemId));
        const price = selected.reduce((total, item) => total + (Number(item.itemPrice || 0) * Number(item.quantity)), 0);
        const quantity = selected.reduce((total, item) => total + Number(item.quantity), 0);
        
        // Lấy địa chỉ hiện tại (ưu tiên tempAddress nếu được chọn)
        const addr = selectedAddressId === 'temp' ? tempAddress : addresses.find(a => a.addressId.toString() === selectedAddressId);

        return { validItemsCount: validItems.length, selectedCartItems: selected, totalPrice: price, totalSelectedQuantity: quantity, currentAddress: addr };
    }, [cartItems, selectedItemIds, addresses, selectedAddressId, tempAddress]);

    const handleQuantityChange = (cartItemId, newQuantity, maxQuantity, isFlashSale, flashSaleLimit) => {
        const limit = isFlashSale ? flashSaleLimit : maxQuantity;
        if (newQuantity < 1 || newQuantity > limit) return;

        setCartItems(prev => prev.map(item => item.cartItemId === cartItemId ? { ...item, quantity: newQuantity } : item));

        if (debounceTimers.current[cartItemId]) clearTimeout(debounceTimers.current[cartItemId]);
        debounceTimers.current[cartItemId] = setTimeout(() => {
            // Tách riêng hàm async chạy bên trong setTimeout
            const updateApi = async () => {
                try {
                    await cartService.updateQuantity(cartItemId, newQuantity);
                    await fetchGlobalCart();
                } catch (error) { 
                    fetchCartData(); 
                }
            };
            updateApi();
        }, 500);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const ids = cartItems.filter(item => {
                const limit = item.isFlashSaleLine ? (item.itemFlashSaleQuantity || 0) : item.maxQuantity;
                return (item.itemStatus === 2 || item.itemStatus === 3) && Number(item.itemPrice) > 0 && item.maxQuantity > 0 && item.quantity <= limit;
            }).map(i => i.cartItemId);
            setSelectedItemIds(ids);
        } else setSelectedItemIds([]);
    };

    const handleSelectItem = (id) => {
        setSelectedItemIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const handleRemoveItem = async (id) => {
        if (!window.confirm("Bỏ sản phẩm này khỏi giỏ hàng?")) return;
        try {
            await cartService.removeFromCart(id);
            setCartItems(prev => prev.filter(i => i.cartItemId !== id));
            setSelectedItemIds(prev => prev.filter(i => i !== id));
            await fetchGlobalCart();
        } catch (error) { toast.error("Lỗi khi xóa!"); }
    };

    const handleRemoveSelectedItems = async () => {
        if (!window.confirm(`Xóa ${selectedItemIds.length} sản phẩm?`)) return;
        try {
            await Promise.all(selectedItemIds.map(id => cartService.removeFromCart(id)));
            setCartItems(prev => prev.filter(item => !selectedItemIds.includes(item.cartItemId)));
            setSelectedItemIds([]);
            await fetchGlobalCart();
        } catch (error) { fetchCartData(); }
    };

    // Form Modal Handlers
    const handleAddressFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setAddressForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSaveNewAddress = async (e) => {
        e.preventDefault();
        if (!addressForm.receiverName || !addressForm.receiverPhone || !addressForm.specificAddress || !addressForm.province) {
            return toast.warn("Vui lòng điền đủ Tên, SĐT, Tỉnh/Thành và Địa chỉ cụ thể!");
        }

        setIsSavingAddress(true);
        const newAddressData = {
            receiverName: addressForm.receiverName.trim(),
            receiverPhone: addressForm.receiverPhone.trim(),
            receiverEmail: addressForm.receiverEmail.trim(),
            province: addressForm.province.trim(),
            ward: addressForm.ward.trim(),
            specificAddress: addressForm.specificAddress.trim()
        };

        try {
            if (addressForm.saveToProfile) {
                // Gọi API tạo mới địa chỉ
                await customerService.createAddress(newAddressData);
                toast.success("Đã thêm vào sổ địa chỉ!");
                fetchUserAddresses(true); // Cập nhật lại danh sách ngầm định
            }

            // Dù lưu hay không, vẫn gán nó làm địa chỉ tạm thời để Checkout luôn cho mượt
            setTempAddress({ addressId: 'temp', ...newAddressData });
            setSelectedAddressId('temp');
            setIsAddrOpen(false);
            setIsAddressModalOpen(false);
            
            // Reset form
            setAddressForm({ receiverName: '', receiverPhone: '', receiverEmail: '', province: '', ward: '', specificAddress: '', saveToProfile: true });
        } catch (error) {
            toast.error("Lỗi khi thêm địa chỉ!");
            console.error(error);
        } finally {
            setIsSavingAddress(false);
        }
    };

    const handleCheckout = (e) => {
        e.preventDefault();
        if (selectedItemIds.length === 0) return toast.warn("Vui lòng chọn sản phẩm!");
        if (!currentAddress) return toast.warn("Vui lòng chọn địa chỉ giao hàng!");

        const itemsToCheckout = selectedCartItems.map(item => {
            const pid = item.itemId || item.id || item.item?.itemId;
            const ref = allProducts.find(p => String(p.itemId) === String(pid));
            const uIds = [...new Set([...(item.saleIds || []), ...(ref?.saleIds || [])].map(String))];
            return {
                ...item,
                promotions: allSales.filter(sale => uIds.includes(String(sale.saleId))),
                activePrice: Number(item.itemPrice) || 0
            };
        });

        navigate('/checkout', { state: { address: currentAddress, items: itemsToCheckout, total: totalPrice } });
    };

    const formatPrice = (p) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Number(p) || 0);

    if (!isLoggedIn) return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header /><div className="flex-grow flex items-center justify-center p-4">
                <div className="bg-white p-10 rounded-2xl shadow-sm text-center max-w-md w-full border border-gray-100">
                    <ShieldAlert size={64} className="text-gray-300 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Vui lòng đăng nhập</h2>
                    <button onClick={() => navigate('/login')} className="bg-tnc-orange text-white font-bold py-3 px-8 rounded-lg w-full mt-4">Đăng nhập</button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />
            <main className="container mx-auto px-4 py-8 max-w-6xl flex-grow">
                <div className="flex items-center gap-2 mb-6">
                    <Link to="/" className="text-gray-500 hover:text-tnc-orange"><ArrowLeft size={20} /></Link>
                    <h1 className="text-2xl font-bold text-[#002855]">Giỏ hàng của bạn</h1>
                </div>

                {loading ? <div className="text-center py-20 font-bold text-tnc-orange">Đang tải...</div> : cartItems.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm p-10 text-center border border-gray-100">
                        <ShoppingBag size={64} className="text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 mb-6">Giỏ hàng đang trống.</p>
                        <Link to="/" className="inline-block bg-tnc-orange text-white font-bold py-2.5 px-6 rounded-lg hover:bg-tnc-orange-hover">Mua sắm ngay</Link>
                    </div>
                ) : (
                    <div className="flex flex-col lg:flex-row gap-8">
                        <div className="lg:w-2/3 bg-gradient-to-r from-orange-100 to-white rounded-xl shadow-sm border border-orange-200/60 p-6">
                            <div className="flex justify-between items-center mb-4 border-b border-orange-200/60 pb-3">
                                <h2 className="font-bold text-lg">Sản phẩm ({cartItems.length})</h2>
                                <div className="flex items-center gap-4">
                                    {selectedItemIds.length > 0 && (
                                        <button onClick={handleRemoveSelectedItems} className="text-sm text-red-500 font-bold flex items-center gap-1.5 bg-red-50 px-3 py-1.5 rounded-lg border border-red-100"><Trash2 size={16} /> Xóa ({selectedItemIds.length})</button>
                                    )}
                                    <label className="flex items-center gap-2 cursor-pointer pl-4 border-l border-gray-200">
                                        <input type="checkbox" checked={selectedItemIds.length === validItemsCount && validItemsCount > 0} onChange={handleSelectAll} disabled={validItemsCount === 0} className="w-4 h-4 accent-tnc-orange" />
                                        <span className="text-sm font-bold text-gray-700">Chọn tất cả hợp lệ</span>
                                    </label>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                {cartItems.map((item) => {
                                    const isFS = !!item.isFlashSaleLine;
                                    const pid = item.itemId || item.id || item.item?.itemId;
                                    const ref = allProducts.find(p => String(p.itemId) === String(pid));
                                    const uIds = [...new Set([...(item.saleIds || []), ...(ref?.saleIds || [])].map(String))];
                                    const promos = allSales.filter(s => uIds.includes(String(s.saleId)));
                                    const canBuy = (item.itemStatus === 2 || item.itemStatus === 3) && item.maxQuantity > 0;

                                    return (
                                        <div key={item.cartItemId} className={isFS ? "flex gap-4 items-start py-4 px-3 rounded-lg border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-white shadow-sm relative transition-all" : "flex gap-4 items-start py-4 px-3 rounded-lg border border-gray-200 bg-white hover:border-tnc-orange transition-all"}>
                                            <input type="checkbox" checked={selectedItemIds.includes(item.cartItemId)} onChange={() => handleSelectItem(item.cartItemId)} disabled={!canBuy} className="w-5 h-5 accent-tnc-orange mt-8" />
                                            <Link to={`/san-pham/${generateSlug(item.name)}-id${item.id}`} className="shrink-0"><img src={getImageUrl(item.image)} alt={item.name} className={`w-20 h-20 object-cover rounded-lg border border-gray-200 ${!canBuy ? 'grayscale' : ''}`} /></Link>
                                            <div className="flex-grow min-w-0">
                                                <h3 className={`font-bold text-sm line-clamp-2 ${!canBuy ? 'text-gray-400' : 'text-gray-800'}`}>{item.name}</h3>
                                                <div className="text-[11px] text-gray-500 mt-1 mb-1.5 font-mono">Mã: {item.itemCode}</div>
                                                {!isFS && promos.length > 0 && <div className="mb-2 space-y-1">{promos.map((p, idx) => <PromotionAccordion key={idx} promo={p} />)}</div>}
                                                <div className="flex items-center gap-2">
                                                    <span className={`font-extrabold text-base ${canBuy ? 'text-tnc-orange' : 'text-gray-400'}`}>{formatPrice(item.itemPrice)}</span>
                                                    {isFS && canBuy && <span className="text-[10px] bg-tnc-orange text-white px-2 py-0.5 rounded font-bold animate-pulse"><Zap size={10} fill="currentColor"/> FLASH SALE</span>}
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0 flex flex-col items-end justify-between">
                                                <span className="font-bold text-base text-[#002855]">{formatPrice(item.itemPrice * item.quantity)}</span>
                                                <div className="flex items-center gap-3 mt-3">
                                                    <div className="flex items-center border border-gray-300 bg-white rounded-md overflow-hidden">
                                                        <button type="button" onClick={() => handleQuantityChange(item.cartItemId, item.quantity - 1, item.maxQuantity, isFS, item.itemFlashSaleQuantity)} className="px-2.5 py-1 font-bold hover:bg-gray-100">-</button>
                                                        <span className="px-2 font-bold text-xs min-w-[25px] text-center">{item.quantity}</span>
                                                        <button type="button" onClick={() => handleQuantityChange(item.cartItemId, item.quantity + 1, item.maxQuantity, isFS, item.itemFlashSaleQuantity)} className="px-2.5 py-1 font-bold hover:bg-gray-100">+</button>
                                                    </div>
                                                    <button type="button" onClick={() => handleRemoveItem(item.cartItemId)} className="text-gray-400 hover:text-red-500"><Trash2 size={20} /></button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="lg:w-1/3">
                            <form onSubmit={handleCheckout} className="bg-gradient-to-b from-orange-50 to-white rounded-xl shadow-lg border border-orange-200/60 p-6 sticky top-24">
                                <div className="flex justify-between items-center mb-4 border-b border-orange-200/60 pb-2">
                                    <h2 className="font-bold text-lg text-[#002855]">Giao tới</h2>
                                </div>
                                
                                <div className="space-y-5 mb-6">
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setIsAddrOpen(!isAddrOpen)}
                                            className={`w-full flex items-center justify-between gap-3 p-4 rounded-xl border-2 transition-all duration-200 ${isAddrOpen ? 'border-tnc-orange bg-white shadow-md' : 'border-blue-100 bg-white hover:border-blue-200'}`}
                                        >
                                            {currentAddress ? (
                                                <div className="text-left overflow-hidden">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-bold text-gray-800 text-sm truncate">{currentAddress.receiverName}</span>
                                                        <span className="text-gray-300">|</span>
                                                        <span className="font-bold text-gray-800 text-sm">{currentAddress.receiverPhone}</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 truncate">{currentAddress.specificAddress}</p>
                                                </div>
                                            ) : <span className="text-sm font-medium text-gray-400">Chọn địa chỉ nhận hàng</span>}
                                            <ChevronDown size={20} className={`text-gray-400 transition-transform duration-300 ${isAddrOpen ? 'rotate-180 text-tnc-orange' : ''}`} />
                                        </button>

                                        {isAddrOpen && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setIsAddrOpen(false)}></div>
                                                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 shadow-2xl rounded-2xl z-20 overflow-hidden animate-in fade-in zoom-in duration-200">
                                                    <div className="max-h-[320px] overflow-y-auto p-2 space-y-1">
                                                        
                                                        {/* Hiển thị địa chỉ tạm (nếu có) */}
                                                        {tempAddress && (
                                                            <button key="temp" type="button" onClick={() => { setSelectedAddressId('temp'); setIsAddrOpen(false); }} className={`w-full flex flex-col p-3 rounded-xl transition-all text-left ${selectedAddressId === 'temp' ? 'bg-orange-50 border border-orange-200' : 'hover:bg-gray-50 border border-transparent'}`} >
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <div className="flex items-center gap-2 font-bold text-sm">
                                                                        {tempAddress.receiverName} 
                                                                        <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full uppercase ml-1">Mới</span>
                                                                    </div>
                                                                    {selectedAddressId === 'temp' && <CheckCircle2 size={16} className="text-tnc-orange" />}
                                                                </div>
                                                                <p className="text-xs text-gray-500 leading-relaxed truncate">{tempAddress.specificAddress}</p>
                                                            </button>
                                                        )}

                                                        {addresses.map(addr => (
                                                            <button key={addr.addressId} type="button" onClick={() => { setSelectedAddressId(addr.addressId.toString()); setIsAddrOpen(false); }} className={`w-full flex flex-col p-3 rounded-xl transition-all text-left ${selectedAddressId === addr.addressId.toString() ? 'bg-orange-50' : 'hover:bg-gray-50'}`} >
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <div className="flex items-center gap-2 font-bold text-sm">
                                                                        {addr.receiverName} 
                                                                        {addr.isDefault && <span className="text-[9px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full uppercase ml-1">Mặc định</span>}
                                                                    </div>
                                                                    {selectedAddressId === addr.addressId.toString() && <CheckCircle2 size={16} className="text-tnc-orange" />}
                                                                </div>
                                                                {addr.receiverEmail && (
                                                                    <div className="flex items-center gap-1 text-[10px] text-gray-400 mb-1">
                                                                        <Mail size={10} /> {addr.receiverEmail}
                                                                    </div>
                                                                )}
                                                                <p className="text-xs text-gray-500 leading-relaxed truncate">{addr.specificAddress}</p>
                                                            </button>
                                                        ))}
                                                        
                                                        <button 
                                                            type="button" 
                                                            onClick={() => {
                                                                setIsAddrOpen(false);
                                                                setIsAddressModalOpen(true);
                                                            }} 
                                                            className="w-full text-center p-3 text-sm font-bold text-blue-600 hover:bg-blue-50 border-t mt-1"
                                                        >
                                                            <Plus size={16} className="inline mr-1" /> Thêm mới
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {currentAddress && (
                                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl relative overflow-hidden group hover:shadow-md transition-shadow">
                                            <div className="absolute top-0 left-0 w-1 h-full bg-[#002855]"></div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="font-bold text-gray-800 text-sm">{currentAddress.receiverName}</span>
                                                <span className="text-gray-300">|</span>
                                                <span className="font-bold text-gray-800 text-sm">{currentAddress.receiverPhone}</span>
                                                {currentAddress.isDefault && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold ml-1 flex items-center gap-1"><CheckCircle2 size={10}/> Mặc định</span>}
                                            </div>

                                            {currentAddress.receiverEmail && (
                                                <div className="flex items-center gap-2 text-xs text-blue-600 font-medium mb-2 pl-1">
                                                    <Mail size={14} className="text-gray-400" />
                                                    {currentAddress.receiverEmail}
                                                </div>
                                            )}

                                            <div className="text-sm text-gray-600 flex flex-col gap-1.5">
                                                <span className="flex items-start gap-2 leading-snug"><MapPin size={16} className="mt-0.5 flex-shrink-0 text-gray-400"/><span>{currentAddress.specificAddress}</span></span>
                                                <span className="pl-6 text-gray-500 italic">{currentAddress.ward ? `${currentAddress.ward}, ` : ''}{currentAddress.province}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="border-t border-gray-100 pt-5 mb-6">
                                    <div className="flex justify-between mb-3 text-sm">
                                        <span className="text-gray-600 font-medium">Sản phẩm đã chọn:</span>
                                        <span className="font-bold text-gray-800">{totalSelectedQuantity} món</span>
                                    </div>
                                    <div className="flex justify-between text-lg font-bold">
                                        <span className="text-[#002855]">Tổng thanh toán:</span>
                                        <span className="text-tnc-orange text-xl">{formatPrice(totalPrice)}</span>
                                    </div>
                                </div>

                                <button type="submit" disabled={selectedItemIds.length === 0 || !currentAddress} className="w-full bg-tnc-orange hover:bg-tnc-orange-hover disabled:bg-gray-200 text-white font-bold py-3.5 rounded-xl transition-all flex justify-center items-center uppercase shadow-lg shadow-orange-200 gap-2">
                                    ĐẶT HÀNG <ChevronRight size={18}/>
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </main>
            <Footer />

            {/* MODAL THÊM ĐỊA CHỈ MỚI */}
            {isAddressModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="bg-[#002855] p-4 text-white flex justify-between items-center shrink-0">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                <MapPin size={20} className="text-tnc-orange" /> Thêm địa chỉ nhận hàng mới
                            </h3>
                            <button onClick={() => setIsAddressModalOpen(false)} className="hover:bg-white/20 p-1.5 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto flex-1 bg-white space-y-4 text-sm">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Tên người nhận <span className="text-red-500">*</span></label>
                                    <input type="text" name="receiverName" value={addressForm.receiverName} onChange={handleAddressFormChange} className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange" placeholder="Nhập tên người nhận" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Số điện thoại <span className="text-red-500">*</span></label>
                                    <input type="text" name="receiverPhone" value={addressForm.receiverPhone} onChange={handleAddressFormChange} className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange" placeholder="Nhập số điện thoại" />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Email <span className="text-gray-400 font-normal">(Không bắt buộc)</span></label>
                                <input type="email" name="receiverEmail" value={addressForm.receiverEmail} onChange={handleAddressFormChange} className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange" placeholder="Nhập email" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Tỉnh/Thành phố <span className="text-red-500">*</span></label>
                                    <input type="text" name="province" value={addressForm.province} onChange={handleAddressFormChange} className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange" placeholder="Ví dụ: Quảng Ninh" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Quận/Huyện/Phường/Xã</label>
                                    <input type="text" name="ward" value={addressForm.ward} onChange={handleAddressFormChange} className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange" placeholder="Ví dụ: Phường Hạ Long" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Địa chỉ cụ thể (Số nhà, Tên đường) <span className="text-red-500">*</span></label>
                                <textarea name="specificAddress" value={addressForm.specificAddress} onChange={handleAddressFormChange} rows="2" className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange resize-none" placeholder="Nhập địa chỉ nhận hàng"></textarea>
                            </div>

                            <div className="pt-2">
                                <label className="flex items-center gap-2 cursor-pointer p-3 bg-orange-50 border border-orange-100 rounded-xl">
                                    <input type="checkbox" name="saveToProfile" checked={addressForm.saveToProfile} onChange={handleAddressFormChange} className="w-4 h-4 accent-tnc-orange shrink-0" />
                                    <span className="text-sm font-bold text-gray-800">Lưu thông tin này vào Sổ địa chỉ để dùng cho các lần sau</span>
                                </label>
                            </div>
                        </div>

                        <div className="p-4 border-t border-orange-200/60 bg-white shrink-0 flex gap-3">
                            <button onClick={() => setIsAddressModalOpen(false)} className="px-6 py-2.5 bg-gray-100 text-gray-600 font-bold rounded-xl hover:bg-gray-200 transition-colors">
                                Hủy
                            </button>
                            <button onClick={handleSaveNewAddress} disabled={isSavingAddress} className="flex-1 bg-tnc-orange text-white font-bold py-2.5 rounded-xl hover:bg-tnc-orange-hover transition-colors disabled:opacity-50">
                                {isSavingAddress ? 'Đang xử lý...' : 'Xác nhận'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CartPage;